"use client";

import dynamic from "next/dynamic";

import type { AdvancedTradingViewChartProps } from "@/components/app/chart/advanced-trading-view-chart";
import { Skeleton } from "@/components/ui/skeleton";
import { useScriptLoader } from "@/contexts/script-loader-context";
import { useResolveUsername } from "@/hooks/use-resolve-username";
import { DESO_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { cn } from "@/utils/lib";

type AdvancedTradingViewChartContainerProps = Omit<
  AdvancedTradingViewChartProps,
  "publicKey"
> & {
  containerClassName?: string;
  publicKey?: string;
};

const AdvancedTradingViewChart = dynamic(
  () =>
    import("@/components/app/chart/advanced-trading-view-chart").then((mod) => {
      return mod.AdvancedTradingViewChart;
    }),
  { ssr: false },
);

export const AdvancedTradingViewChartContainer = ({
  containerClassName,
  ...props
}: AdvancedTradingViewChartContainerProps) => {
  const lowercaseUsername =
    props.username.toLowerCase() === DESO_TOKEN_DISPLAY_NAME.toLowerCase();
  const { publicKey: resolvedPublicKey } = useResolveUsername(
    props.username,
    !!props.publicKey || lowercaseUsername,
  );
  const isChartScriptReady = useScriptLoader({
    src: "/static/datafeeds/udf/dist/bundle.js",
  });

  const publicKey = lowercaseUsername
    ? process.env.NEXT_PUBLIC_DESO_ZERO_PUBLIC_KEY
    : props.publicKey || resolvedPublicKey;

  return (
    <div className={cn("rounded-2xl")}>
      <div className="rounded-2xl border border-border">
        {isChartScriptReady && publicKey ? (
          <AdvancedTradingViewChart {...props} publicKey={publicKey} />
        ) : (
          <Skeleton className={cn("h-[450px]", containerClassName)} />
        )}
      </div>
    </div>
  );
};
